import { getState, setState } from '../store';
import { getLeaderboard } from '../api/game-api';
import { navigate } from '../router';
import { escapeHtml } from '../utils/escape';

export async function renderLeaderboardView(container: HTMLElement): Promise<void> {
  const state = getState();

  // Session 69 — graceful fallback when /leaderboard is opened without an
  // active session. Can happen after main.ts boot guard wipes a stale
  // FINISHED snapshot: the hash was `#/leaderboard`, the router lands
  // here, but state.gameCode is empty. Without this branch the view
  // tries `getLeaderboard('', '')` and lands in the generic error UI.
  if (!state.gameCode || !state.playerId) {
    container.innerHTML = `
      <div class="screen screen-enter" style="justify-content: center; align-items: center;">
        <div style="text-align: center; max-width: 400px; padding: var(--sp-xl);">
          <p class="text-display-medium" style="color: var(--amber); margin-bottom: var(--sp-md);">
            ЛИДЕРБОРД
          </p>
          <p class="text-body" style="color: var(--text-secondary); margin-bottom: var(--sp-xl);">
            Нет активной игры. Присоединитесь к квесту, чтобы увидеть результаты команд.
          </p>
          <button id="lb-empty-join" class="btn btn-primary btn-full">К ВХОДУ В ИГРУ</button>
        </div>
      </div>
    `;
    container.querySelector('#lb-empty-join')!.addEventListener('click', () => {
      navigate('join');
    });
    return;
  }

  container.innerHTML = `
    <div class="screen screen-enter">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--sp-lg);">
        <button id="lb-back" class="btn btn-outline" style="min-height: var(--touch-md); padding: var(--sp-sm) var(--sp-md); font-size: 14px;">Назад</button>
        <h1 class="text-display-medium" style="color: var(--amber);">ЛИДЕРБОРД</h1>
        <button id="lb-refresh" class="btn btn-secondary" style="min-height: 40px; padding: 8px 16px; font-size: 14px;">↻</button>
      </div>

      <div id="lb-content"></div>
    </div>
  `;

  container.querySelector('#lb-back')!.addEventListener('click', () => {
    if (state.playerRole === 'SPECTATOR') navigate('spectator');
    else navigate('results');
  });

  container.querySelector('#lb-refresh')!.addEventListener('click', () => loadData());

  async function loadData(): Promise<void> {
    const contentEl = container.querySelector<HTMLElement>('#lb-content')!;

    contentEl.innerHTML = `
      <div style="text-align: center; padding: var(--sp-xl);">
        <div class="spinner" style="margin: 0 auto var(--sp-md);"></div>
        <p class="text-body" style="color: var(--text-secondary);">Загрузка...</p>
      </div>
      <style>.spinner { width: 24px; height: 24px; border: 3px solid var(--bg-border); border-top-color: var(--amber); border-radius: 50%; animation: spin 0.8s linear infinite; }
      @keyframes spin { to { transform: rotate(360deg); } }</style>
    `;

    try {
      const entries = await getLeaderboard(state.gameCode, state.playerId);
      setState({ leaderboard: entries });
      renderEntries(contentEl, entries);
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      const userMsg = humanizeLeaderboardError(raw);
      contentEl.innerHTML = `
        <div style="text-align: center; padding: var(--sp-xl);">
          <p class="text-body" style="color: var(--danger); margin-bottom: var(--sp-md);">
            ${escapeHtml(userMsg)}
          </p>
          <button id="lb-retry" class="btn btn-secondary">Повторить</button>
        </div>
      `;
      contentEl.querySelector('#lb-retry')?.addEventListener('click', () => loadData());
    }
  }

  loadData();
}

function humanizeLeaderboardError(raw: string): string {
  if (raw === 'HTTP 404') return 'Лидерборд пока не готов — ещё никто не финишировал.';
  if (raw === 'HTTP 403') return 'Нет доступа к лидерборду.';
  if (raw === 'HTTP 401') return 'Сессия истекла. Войдите заново.';
  if (raw.startsWith('HTTP ')) return 'Не удалось загрузить лидерборд. Попробуйте ещё раз.';
  return raw || 'Ошибка загрузки';
}

export function renderEntries(container: HTMLElement, entries: Array<{
  teamName: string;
  locationsCompleted: number;
  totalLocations: number;
  totalTimeMs: number;
  // v0.79.39 (2026-05-03) — SIGNED net. Positive → «Штраф +M:SS» (red),
  // negative → «Бонус -M:SS» (green), 0 → no chip. Per-tx clamp dropped
  // because bonuses past full-pardon silently vanished (live game 983541
  // «Кодоломы»: org applied −45 min, only −14 min stuck pre-fix).
  penaltyMs?: number;
  wrongAnswers: number;
  finished: boolean;
}>): void {
  if (entries.length === 0) {
    container.innerHTML = `<p class="text-body" style="color: var(--text-hint); text-align: center; padding: var(--sp-xl);">Нет данных</p>`;
    return;
  }

  const medals = ['🥇', '🥈', '🥉'];
  const medalColors = ['var(--amber)', '#C0C0C0', '#CD7F32'];

  container.innerHTML = entries.map((e, i) => {
    const rank = i + 1;
    const isTop3 = rank <= 3;
    const totalSec = Math.floor(e.totalTimeMs / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    const timeStr = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

    // v0.79.39 — single signed chip from `penaltyMs`. The server-side
    // per-transaction clamp on `Teams.penaltyMs` was removed in v0.79.39,
    // so this field is now the SIGNED net (positive = net penalty,
    // negative = net pardon). Render as «Штраф +M:SS» (red) when > 0,
    // «Бонус -M:SS» (green) when < 0, hidden when 0. Backend folds
    // `penaltyMs` into `totalTimeMs` (also clamped at 0 there to keep
    // wall-clock time non-negative even with extreme bonuses).
    const penaltyMs = e.penaltyMs || 0;
    let penaltyStr = '';
    let bonusStr = '';
    if (penaltyMs > 0) {
      const pSec = Math.floor(penaltyMs / 1000);
      const pm = Math.floor(pSec / 60);
      const ps = pSec % 60;
      penaltyStr = `+${String(pm).padStart(2, '0')}:${String(ps).padStart(2, '0')}`;
    } else if (penaltyMs < 0) {
      const absMs = -penaltyMs;
      const bSec = Math.floor(absMs / 1000);
      const bm = Math.floor(bSec / 60);
      const bs = bSec % 60;
      bonusStr = `-${String(bm).padStart(2, '0')}:${String(bs).padStart(2, '0')}`;
    }

    return `
      <div class="card" style="margin-bottom: var(--sp-sm); ${isTop3 ? `border: 1px solid ${medalColors[i]};` : ''}">
        <div style="display: flex; align-items: center; gap: var(--sp-md);">
          <div style="width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
                      background: ${isTop3 ? medalColors[i] : 'var(--bg-border)'}; color: ${isTop3 ? 'var(--bg-deep)' : 'var(--text-primary)'};
                      font-family: var(--font-mono); font-weight: 600; font-size: ${isTop3 ? '20px' : '16px'};">
            ${isTop3 ? medals[i] : rank}
          </div>
          <div style="flex: 1;">
            <p class="text-title" style="${isTop3 ? 'font-weight: 700;' : ''}">${escapeHtml(e.teamName)}</p>
            <div style="display: flex; gap: var(--sp-md); margin-top: var(--sp-xs); flex-wrap: wrap;">
              <span class="text-data" style="font-size: 14px;">${e.locationsCompleted}/${e.totalLocations}</span>
              ${e.finished ? '<span class="text-label" style="color: var(--success); font-size: 12px;">ФИНИШ</span>' : ''}
              ${e.wrongAnswers > 0 ? `<span class="text-data" style="font-size: 14px; color: var(--danger);">✗ ${e.wrongAnswers}</span>` : ''}
              ${penaltyStr ? `<span class="text-data" style="font-size: 14px; color: var(--danger);">Штраф ${penaltyStr}</span>` : ''}
              ${bonusStr ? `<span class="text-data" style="font-size: 14px; color: var(--success);">Бонус ${bonusStr}</span>` : ''}
            </div>
          </div>
          <span class="text-data" style="${rank === 1 ? 'font-weight: 700; color: var(--amber);' : 'color: var(--text-secondary);'}">${timeStr}</span>
        </div>
      </div>
    `;
  }).join('');
}
